import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UseTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  template: {
    _id: string;
    name: string;
    description?: string;
  } | null;
}

/**
 * Dialog for using a template to generate a new document
 * Collects user instructions and navigates to document writer with template context
 */
export function UseTemplateDialog({ isOpen, onClose, template }: UseTemplateDialogProps) {
  const [instructions, setInstructions] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleGenerate = () => {
    if (!template) return;

    setIsLoading(true);

    // Navigate to document writer with template context
    navigate("/document-writer", {
      state: {
        templateId: template._id,
        templateName: template.name,
        userInstructions: instructions.trim(),
        generateFromTemplate: true,
      },
    });

    // Reset and close
    setInstructions("");
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      setInstructions("");
      onClose();
    }
  };

  if (!template) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Use Template: {template.name}
          </DialogTitle>
          <DialogDescription>
            {template.description ||
              "Describe what you want to create using this template as a structural guide."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="template-instructions">What would you like to create?</Label>
            <Textarea
              id="template-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g., Create a proposal for ABC Company for monthly cleaning services at their new 50,000 sq ft warehouse..."
              rows={5}
              disabled={isLoading}
              className="resize-none"
            />
            <p className="text-xs text-gray-500">
              The AI will use this template's structure and styling while generating content
              tailored to your instructions.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={isLoading}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate Document
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
